import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { useLang, tr } from "../contexts/LangContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Check, MapPin, FileText, Download } from "lucide-react";

export default function LeClub() {
  const { t, lang, img, visible, sectionOrder } = useLang();
  const ord = (id) => sectionOrder("club", id, ["history", "board", "dues", "admission", "privacy"]);
  const [board, setBoard] = useState([]);
  const [dues, setDues] = useState([]);
  const [admissionPdfs, setAdmissionPdfs] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", birthdate: "", membership_type: "adult", motivation: "",
  });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/board").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/club-dues").then((r) => setDues(r.data)).catch(() => {});
    api.get("/pdfs", { params: { category: "admission" } }).then((r) => setAdmissionPdfs(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/admissions", form);
      setSent(true);
      setForm({ first_name: "", last_name: "", email: "", phone: "", address: "", birthdate: "", membership_type: "adult", motivation: "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="club-page" className="flex flex-col">
      <PageHeader eyebrow={t.club.eyebrow} title={t.club.title} lead={t.club.lead}
        image={img("club", "header", "https://images.unsplash.com/photo-1503634192480-e77a6436f075?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")} />

      {/* History + Location */}
      {visible("club", "history") && (
      <section style={{ order: ord("history") }} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <div className="label-eyebrow text-club-red mb-4">{t.club.historyTitle}</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy mb-4">1928 — 2026</h2>
            <div className="divider-gold mb-6" />
            <p className="text-slate-600 leading-relaxed">{t.club.historyText}</p>
          </div>
          <div>
            <div className="label-eyebrow text-club-red mb-4">{t.club.locationTitle}</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy mb-4 flex items-center gap-3">
              <MapPin className="w-7 h-7 text-club-yellow" strokeWidth={1.5} /> Île Monsin
            </h2>
            <div className="divider-gold mb-6" />
            <p className="text-slate-600 leading-relaxed">{t.club.locationText}</p>
          </div>
        </div>
      </section>
      )}

      {/* Board */}
      {visible("club", "board") && (
      <section style={{ order: ord("board") }} className="py-20 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow text-club-red mb-4">{t.club.boardTitle}</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy">{t.club.boardSub}</h2>
          <div className="divider-gold mt-5" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mt-12">
            {board.map((m) => (
              <div key={m.id} data-testid={`board-${m.id}`}>
                <div className="aspect-[4/5] overflow-hidden bg-slate-200">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                </div>
                <div className="mt-3">
                  <div className="font-serif text-base text-navy leading-tight">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-club-red mt-1">{m[`role_${lang}`]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Dues */}
      {visible("club", "dues") && (
      <section style={{ order: ord("dues") }} className="py-20 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow text-club-yellow mb-4">{t.club.duesTitle}</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-light">{t.club.duesSub}</h2>
          <div className="divider-gold mt-5" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
            {dues.map((d) => (
              <div key={d.id} className="bg-white/5 border border-white/10 p-8 hover:border-club-yellow transition-colors" data-testid={`dues-${d.id}`}>
                <div className="font-serif text-3xl text-club-yellow mb-2">{d.price}</div>
                <div className="text-base mb-2">{d[`name_${lang}`] || d.name_fr}</div>
                <div className="text-xs text-slate-400">{d[`note_${lang}`] || d.note_fr}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Admission */}
      {visible("club", "admission") && (
      <section style={{ order: ord("admission") }} id="admission" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow text-club-red mb-4">{t.club.admissionTitle}</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-light text-navy">{t.club.admissionTitle}</h2>
          <div className="divider-gold mt-5" />
          <p className="text-slate-600 mt-6">{t.club.admissionSub}</p>

          {/* Welcome message + downloadable admission form */}
          <div className="mt-10 bg-sand border-l-4 border-club-yellow p-6 md:p-8" data-testid="admission-info">
            <div className="flex items-start gap-4">
              <FileText className="w-8 h-8 text-club-red shrink-0 mt-1" strokeWidth={1.5} />
              <div className="flex-1">
                <h3 className="font-serif text-xl text-navy mb-2">{t.club.welcomeTitle}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {t.club.welcomeText}
                </p>
                {admissionPdfs.length === 0 ? (
                  <div className="mt-4 text-xs text-slate-500 italic">{tr("Le formulaire sera disponible prochainement.")}</div>
                ) : (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {admissionPdfs.map((p) => (
                      <a
                        key={p.id}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api${p.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-club-red text-white px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark"
                        data-testid={`admission-pdf-${p.id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> {p.title || "Demande d'admission (PDF)"}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admission-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label={t.admission.firstName} value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="admission-first-name" />
              <FormField label={t.admission.lastName} value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="admission-last-name" />
              <FormField label={t.admission.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="admission-email" />
              <FormField label={t.admission.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="admission-phone" />
              <FormField label={t.admission.address} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="admission-address" />
              <FormField label={t.admission.birthdate} type="date" value={form.birthdate} onChange={(v) => setForm({ ...form, birthdate: v })} testid="admission-birthdate" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.admission.type}</label>
              <select value={form.membership_type} onChange={(e) => setForm({ ...form, membership_type: e.target.value })} className="w-full border border-slate-300 focus:border-navy outline-none py-3 px-3 bg-white" data-testid="admission-type">
                {Object.entries(t.admission.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.admission.motivation}</label>
              <textarea rows={4} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} className="w-full border border-slate-300 focus:border-navy outline-none py-3 px-3 bg-white resize-none" data-testid="admission-motivation" />
            </div>

            {err && <div className="text-sm text-red-600" data-testid="admission-error">{err}</div>}
            {sent && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-3" data-testid="admission-success">
                <Check className="w-4 h-4" /> {t.admission.success}
              </div>
            )}

            <button type="submit" className="bg-navy text-white px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors" data-testid="admission-submit">
              {t.admission.submit}
            </button>
          </form>
        </div>
      </section>
      )}

      {/* Privacy */}
      {visible("club", "privacy") && (
      <section style={{ order: ord("privacy") }} className="py-20 bg-sand">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="label-eyebrow mb-4">{t.club.privacyTitle}</div>
          <h2 className="font-serif text-2xl lg:text-3xl font-light text-navy">{t.club.privacyTitle}</h2>
          <div className="divider-gold mt-5" />
          <p className="text-slate-600 mt-6 leading-relaxed">{t.club.privacyText}</p>
        </div>
      </section>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent transition-colors"
        data-testid={testid} />
    </div>
  );
}
